import DOMPurify from 'dompurify';

interface TrustedTypesAPI {
  createPolicy: (
    name: string,
    rules: {
      createHTML?: (s: string) => string;
      createScriptURL?: (s: string) => string;
      createScript?: (s: string) => string;
    },
  ) => unknown;
}

declare global {
  interface Window {
    trustedTypes?: TrustedTypesAPI;
  }
}

const POLICY_NAMES = ['default', 'sentinel-spa'] as const;

function buildRules() {
  return {
    createHTML: (s: string): string =>
      DOMPurify.sanitize(s, { RETURN_TRUSTED_TYPE: false }) as unknown as string,
    createScriptURL: (s: string): string => {
      const origin = typeof location !== 'undefined' ? location.origin : '';
      const u = new URL(s, origin || 'http://localhost');
      if (origin && u.origin !== origin) {
        throw new TypeError(`Trusted Types: blocked cross-origin scriptURL ${u.origin}`);
      }
      return s;
    },
    createScript: (_s: string): string => {
      throw new TypeError('Trusted Types: createScript is disabled in the sentinel SPA');
    },
  };
}

/**
 * Register the `default` and `sentinel-spa` Trusted Types policies for the
 * sentinel SPA. The CSP at the gateway emits `require-trusted-types-for
 * 'script'` plus `trusted-types default sentinel-spa`, so every DOM
 * script-execution sink will refuse strings that did not flow through one of
 * the registered policies. Call this **before** any React import is
 * evaluated so React's internal sink writes use the `default` policy.
 *
 * Browsers without Trusted Types support ignore the directive and this
 * function becomes a no-op.
 */
export function installTrustedTypesPolicies(): void {
  const api = (globalThis as { trustedTypes?: TrustedTypesAPI }).trustedTypes;
  if (!api) return;
  const rules = buildRules();
  for (const name of POLICY_NAMES) {
    try {
      api.createPolicy(name, rules);
    } catch {
      // Trusted Types throws for duplicate-name registrations (e.g.
      // hot-module-reload re-run). Safe to ignore.
    }
  }
}
